import { StackWithDividers } from '@components'

import { Divider, Flex, IconButton, notify, Space, Text, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { usePredefinedPaymentMethodsValues } from '@features/invoicingSettings/modules/paymentsAndReminders'
import { usePayNowStatus } from '@features/paynow/hooks/usePayNowStatus'
import { useUpdatePayNowActiveStatus } from '@features/paynow/hooks/useUpdatePayNowActiveStatus'

import { ModalId } from '../../../../../../../../../../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../../../../../../../../../../enums/notificationKeys'
import { PaymentMethodsListItem } from './elements/PaymentMethodsListItem'

export const PaymentMethodsList = (): ReactElement => {
  const { t } = useTranslation()
  const { predefinedPaymentMethodsValues } = usePredefinedPaymentMethodsValues()
  const { open: openPaymentMethodsModal } = useModal(ModalId.InvoiceEditorPaymentMethodsModal)

  const { isConnected: shouldDisplayCardPayments } = usePayNowStatus()
  const { isProcessing, updatePayNowActiveStatus } = useUpdatePayNowActiveStatus()

  const { bank, offlinePayment, iban, paypalEmail, swift } = predefinedPaymentMethodsValues

  const bankValues = [bank, iban, swift].filter(Boolean)
  const shouldDisplayBank = !!bankValues.length
  const bankDetails = bankValues.join(', ')

  const handleEditButtonClick = useCallback(() => {
    openPaymentMethodsModal()
  }, [openPaymentMethodsModal])

  const handleCardPaymentToggle = useCallback(
    (checked: boolean) => {
      updatePayNowActiveStatus(
        { active: checked },
        {
          onError: () => {
            notify({
              id: NotificationKeys.PayNowStatusUpdateError,
              message: t('settings.organization.card_payments.configuration.allow_card.update_error'),
              variant: 'error',
            })
          },
        },
      )
    },
    [t, updatePayNowActiveStatus],
  )

  return (
    <Flex flexDirection="column">
      <Flex alignItems="center" justifyContent="space-between">
        <Text colorVariant="secondary">{t('external_invoices.editor.payment_methods.list.your_payments')}</Text>
        <IconButton icon="squarePen" onClick={handleEditButtonClick} />
      </Flex>
      <Space size="s6" />
      <Divider />
      <StackWithDividers divider={<Divider />}>
        {shouldDisplayBank && (
          <PaymentMethodsListItem
            details={bankDetails}
            formValueName="displayedPaymentMethods.bank"
            icon="bank"
            title={t('external_invoices.editor.payment_methods.list.bank_transfer')}
          />
        )}
        {paypalEmail && (
          <PaymentMethodsListItem
            details={paypalEmail}
            formValueName="displayedPaymentMethods.paypal"
            icon="paypal"
            title={t('external_invoices.editor.payment_methods.list.paypal')}
          />
        )}
        {offlinePayment && (
          <PaymentMethodsListItem
            details={offlinePayment}
            formValueName="displayedPaymentMethods.offlinePayment"
            icon="banknote"
            title={t('external_invoices.editor.payment_methods.list.offline_payment')}
          />
        )}
        {shouldDisplayCardPayments && (
          <PaymentMethodsListItem
            details={t('external_invoices.editor.payment_methods.list.card_payments.description')}
            formValueName="displayedPaymentMethods.stripeCardPayment"
            icon="creditCard"
            isDisabled={isProcessing}
            onToggle={handleCardPaymentToggle}
            title={t('external_invoices.editor.payment_methods.list.card_payments.title')}
          />
        )}
      </StackWithDividers>
    </Flex>
  )
}
